import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import CookieService from "../../context/cookies";
import { base } from "../../constants/apis";
import { EstimatedQuoteRes, FileAnalysisRes } from "../../types/Logistics";
import { LogisticsRequestQuote } from "../../types/PartnerCompanyDetails";
import { ApiResponse } from "../../types/ApiResponse";
import { AIParseFilePayload } from "../../types/DealTracker";

export const logisticesApi = createApi({
  reducerPath: "logisticesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${base}/apis`,
    prepareHeaders: (headers) => {
      const token = CookieService.getCookie("jwtToken");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["logisticesApi", "requestedLogisticsQuotesApi"],
  endpoints: (builder) => ({
    getEstimatedQuote: builder.mutation<EstimatedQuoteRes, any>({
      query: (payload) => ({
        url: "/ai/get_estimated_quote.php",
        method: "POST",
        body: payload,
      }),
    }),

    getAIAnalyzedFileData: builder.mutation<FileAnalysisRes, any>({
      query: (payload) => ({
        url: "/ai/checkdocs.php",
        method: "POST",
        body: payload,
      }),
      transformErrorResponse: (response: {
        status: number;
        data: { error: string };
      }): string | number => response.data.error,
    }),

    requestLogisticsQuotes: builder.mutation({
      query: (payload) => ({
        url: "/logistics_request_quotes/add.php",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["requestedLogisticsQuotesApi"],
    }),

    getRequestedLogisticsQuotes: builder.query<LogisticsRequestQuote[], void>({
      query: () => "/logistics_request_quotes/get.php",
      transformResponse: (
        res: ApiResponse<LogisticsRequestQuote[]> & {
          trade_flow_quotes: LogisticsRequestQuote[];
        },
      ) => {
        // const allQuotes = res.data.concat(res.trade_flow_quotes);
        const allQuotes = res.data;

        return allQuotes.sort((a, b) => {
          const aTime = a.created_at ? new Date(a.created_at).getTime() : null;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : null;

          // both missing → keep relative order
          if (!aTime && !bTime) return 0;

          // only a missing → a goes below
          if (!aTime) return 1;

          // only b missing → b goes below
          if (!bTime) return -1;

          // latest first
          return bTime - aTime;
        });
      },
      providesTags: ["requestedLogisticsQuotesApi"],
    }),

    updateLogisticsQuote: builder.mutation({
      query: (payload) => ({
        url: "/logistics_request_quotes/quotes_update.php",
        method: "POST",
        body: payload,
      }),

      invalidatesTags: ["requestedLogisticsQuotesApi"],
    }),

    AIParseFile: builder.mutation<any, AIParseFilePayload>({
      query: (payload) => ({
        url: "/ai/parse_file.php",
        method: "POST",
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetEstimatedQuoteMutation,
  useGetAIAnalyzedFileDataMutation,
  useRequestLogisticsQuotesMutation,
  useGetRequestedLogisticsQuotesQuery,
  useUpdateLogisticsQuoteMutation,

  useAIParseFileMutation,
} = logisticesApi;
